import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis } from 'recharts';
import { dashboardAPI } from '../services/api';

const COLORS = ['#10b981','#3b82f6','#f59e0b','#f97316','#ef4444'];
const LEVEL_NAMES = ['Low','Mild','Moderate','High','Critical'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-xl p-3 shadow-2xl">
      <p className="text-slate-400 text-xs mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold" style={{ color: p.color || '#fff' }}>{p.name}: {typeof p.value === 'number' ? p.value.toFixed(2) : p.value}</p>
      ))}
    </div>
  );
};

const CustomPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
  const RADIAN = Math.PI / 180;
  const x = cx + (outerRadius + 24) * Math.cos(-midAngle * RADIAN);
  const y = cy + (outerRadius + 24) * Math.sin(-midAngle * RADIAN);
  if (percent < 0.05) return null;
  return <text x={x} y={y} fill="#94a3b8" textAnchor={x > cx ? 'start' : 'end'} fontSize={11} fontWeight="600">{name} {(percent*100).toFixed(0)}%</text>;
};

function ModelCard({ name, accuracy, trees, depth, color, icon, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  const [bar, setBar] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), delay);
    const t2 = setTimeout(() => setBar(accuracy), delay + 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [delay, accuracy]);
  return (
    <div className={`bg-slate-800/60 border rounded-2xl p-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ borderColor: `${color}40` }}>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-3xl">{icon}</span>
        <div>
          <div className="font-black text-white text-lg">{name}</div>
          <div className="text-xs text-slate-400">{trees} · {depth}</div>
        </div>
        <div className="ml-auto text-3xl font-black" style={{ color }}>{accuracy}%</div>
      </div>
      <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${bar}%`, background: `linear-gradient(90deg, ${color}99, ${color})` }}/>
      </div>
      <div className="flex justify-between text-xs text-slate-500 mt-2">
        <span>Accuracy</span><span>{accuracy}%</span>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await dashboardAPI.getCharts();
      setCharts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="relative w-24 h-24 mx-auto mb-6">
          {[0,1,2].map(i => <div key={i} className="absolute inset-0 rounded-full border-2 border-purple-500/40 animate-ping" style={{ animationDelay: `${i*0.2}s` }}/>)}
          <div className="absolute inset-0 flex items-center justify-center text-4xl">🔬</div>
        </div>
        <p className="text-slate-400 text-lg font-medium">Loading Analytics...</p>
      </div>
    </div>
  );

  if (!charts) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center bg-slate-800/60 border border-slate-700 rounded-3xl p-12">
        <div className="text-7xl mb-4">📉</div>
        <h2 className="text-2xl font-bold text-white mb-2">No Analytics Yet</h2>
        <p className="text-slate-400">Make predictions to generate analytics</p>
        <a href="/prediction" className="mt-6 inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-xl font-semibold hover:scale-105 transition-transform">Go to Prediction →</a>
      </div>
    </div>
  );

  const stressDistData = Object.entries(charts.stress_distribution).map(([level, count]) => ({
    name: LEVEL_NAMES[parseInt(level)] || `Level ${level}`, value: count
  }));

  const featureData = charts.feature_importance
    .map(f => ({ name: f.feature.toUpperCase(), importance: parseFloat((f.importance * 100).toFixed(2)) }))
    .sort((a, b) => b.importance - a.importance);

  const radarData = featureData.map(f => ({ feature: f.name, value: f.importance }));

  const modelData = [
    { model: 'Random Forest', accuracy: 85.5 },
    { model: 'XGBoost', accuracy: 83.2 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="text-center py-10 px-4">
        <div className="inline-flex items-center gap-3 bg-slate-800/60 border border-slate-700 rounded-2xl px-6 py-3 mb-4">
          <span className="text-2xl">🔬</span>
          <span className="text-sm font-semibold text-slate-300 tracking-widest uppercase">AI Model Analytics</span>
        </div>
        <h1 className="text-5xl font-black bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 bg-clip-text text-transparent mb-3">Analytics</h1>
        <p className="text-slate-400 text-lg">Deep model performance & feature intelligence</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-16 space-y-6">

        {/* Model Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <ModelCard name="Random Forest" accuracy={85.5} trees="100 Trees" depth="Max Depth: 10" color="#3b82f6" icon="🌲" delay={0}/>
          <ModelCard name="XGBoost"       accuracy={83.2} trees="100 Estimators" depth="LR: 0.1" color="#8b5cf6" icon="⚡" delay={150}/>
          <div className="bg-slate-800/60 border border-emerald-500/30 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">🔍</span>
              <div>
                <div className="font-black text-white text-lg">Feature Explainer</div>
                <div className="text-xs text-slate-400">Importance-based analysis</div>
              </div>
            </div>
            <div className="space-y-2 mt-2">
              {featureData.slice(0,3).map((f,i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="text-xs text-slate-400 w-10">{f.name}</div>
                  <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400" style={{ width: `${f.importance}%` }}/>
                  </div>
                  <div className="text-xs text-emerald-400 font-bold w-10 text-right">{f.importance}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Stress Distribution Pie */}
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">🥧</div>
              <div>
                <h3 className="font-bold text-white">Stress Distribution</h3>
                <p className="text-slate-400 text-xs">Breakdown by risk level</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={stressDistData} cx="50%" cy="50%" outerRadius={95} innerRadius={45}
                  dataKey="value" labelLine={false} label={<CustomPieLabel/>}>
                  {stressDistData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="transparent"/>)}
                </Pie>
                <Tooltip content={<CustomTooltip/>}/>
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {stressDistData.map((d, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs text-slate-400">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i] }}/>
                  {d.name}
                </div>
              ))}
            </div>
          </div>

          {/* Model Accuracy */}
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">🏆</div>
              <div>
                <h3 className="font-bold text-white">Model Accuracy</h3>
                <p className="text-slate-400 text-xs">RF vs XGBoost comparison</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={modelData} barSize={60}>
                <defs>
                  <linearGradient id="rfGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6"/><stop offset="100%" stopColor="#1d4ed8"/>
                  </linearGradient>
                  <linearGradient id="xgbGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6"/><stop offset="100%" stopColor="#6d28d9"/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                <XAxis dataKey="model" stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }}/>
                <YAxis domain={[75, 90]} stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Bar dataKey="accuracy" radius={[8,8,0,0]} name="Accuracy %"
                  fill="url(#rfGrad)"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Feature Importance + Radar */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">📊</div>
              <div>
                <h3 className="font-bold text-white">Feature Importance</h3>
                <p className="text-slate-400 text-xs">Random Forest weights</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={featureData} layout="vertical" barSize={14}>
                <defs>
                  <linearGradient id="featGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#f59e0b"/><stop offset="100%" stopColor="#ef4444"/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                <XAxis type="number" stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 10 }}/>
                <YAxis dataKey="name" type="category" width={55} stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }}/>
                <Tooltip content={<CustomTooltip/>}/>
                <Bar dataKey="importance" fill="url(#featGrad)" radius={[0,6,6,0]} name="Importance %"/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-slate-800/60 border border-slate-700/50 rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">🕸️</div>
              <div>
                <h3 className="font-bold text-white">Feature Radar</h3>
                <p className="text-slate-400 text-xs">Multi-dimensional importance</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#1e293b"/>
                <PolarAngleAxis dataKey="feature" tick={{ fill: '#94a3b8', fontSize: 10 }}/>
                <Radar name="Importance" dataKey="value" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.25} strokeWidth={2}/>
                <Tooltip content={<CustomTooltip/>}/>
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
